import { useMemo, useState } from 'react';
import { useAorText } from 'utils/aorTerminology';
import { Controller, useForm, useWatch } from 'react-hook-form';
import type { Feature, Point as GeoJsonPoint } from 'geojson';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  USCGResourceKindOptions,
  TypeOptions,
} from 'components/IncidentConfig/ResourceTypes';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { useStrikeTeams } from 'components/IAP/hooks/strikeTeamHooks';
import {
  mapResourceSetToEditPayload,
  type CreateResourceSetFormData,
  type ResourceSet,
  type ResourceThreatChecklists,
  useCreateResourceSet,
  useResourceSets,
  useUpdateResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';
import type { GroupRosterItem } from 'hooks/incident/useRoster';
import { useAors } from 'hooks/useAors';
import { ChecklistConfigEditor } from '../EventsTab/ChecklistConfigEditor';
import {
  dualWriteFromRuleSelection,
  resolveResourceAutomationRuleChecklists,
} from '../EventsTab/automationRuleChecklists';
import { useOrgEventAutomationRules } from 'hooks/orgEvents/orgEventAutomationHooks';
import {
  CHECK_IN_STATUS_OPTIONS,
  isCheckInStatusEditable,
  normalizeCheckInStatus,
  persistCheckInStatus,
} from './checkInStatus';
import { ASSET_STATUS_FILTER_OPTIONS } from './resourceFilterOptions';
import { ResourceThreatsMultiSelect } from './ResourceThreatsField';
import type { ResourceRow } from './ResourceTabCard';
import {
  USCG_SYMBOLOGY_OPTIONS,
  type UscgSymbol,
} from '../LayersTab/utils/uscgSymbology';
import { ArcgisFeatureLocationField } from './ArcgisFeatureLocationField';
import {
  parseArcgisLocationLink,
  type ArcgisLocationLink,
} from './arcgisFeatureLocation';
import { AssetMediaSection } from './AssetMediaSection';
import { DEFAULT_ASSET_DOCUMENT_FOLDERS } from './assetDocumentFolders';
import { useEnsureAssetOrgFolders } from './useEnsureAssetOrgFolders';
import { useHwcgFlag } from 'utils/featureFlags';
import { observability } from 'utils/observability';
import GeoLocationComponent from 'components/GeoLocationComponent/GeoLocationComponent';
import {
  buildGeoLocationFromGeoJson,
  type GeoLocation,
} from 'components/GeoLocationComponent/buildGeoLocationFromGeoJson';

const NONE = '__none__';
const EXCLUDED_SYMBOLS: UscgSymbol[] = [
  'completedBoom',
  'proposedBoom',
  'oilSpill',
];

type DhsResourceEditFormValues = {
  name: string;
  kind: string;
  type: string;
  assetStatus: string;
  status: string;
  incidentId: string;
  owningOrganization: string;
  description: string;
  capabilities: string;
  address: string;
  dateTimeOrdered: string;
  pointOfContact: string;
  quantity: string;
  unit: string;
  hullTailNumber: string;
  aor: string;
  opcon: string;
  tacon: string;
  symbology: string;
  latitude: string;
  longitude: string;
  eta: string;
  notes: string;
  threats: string[];
  threatChecklists: ResourceThreatChecklists;
};

/** A stored point, in the shape the location picker reads and writes. */
function pointGeoLocation(
  latitude: number,
  longitude: number,
  address?: string
): GeoLocation {
  const geometry: GeoJsonPoint = {
    type: 'Point',
    coordinates: [longitude, latitude],
  };
  return buildGeoLocationFromGeoJson({
    geometry,
    centroid: { type: 'Feature', geometry, properties: {} },
    address,
  });
}

function centroidCoordinates(
  centroid: Feature | undefined
): [number, number] | undefined {
  const geometry = centroid?.geometry;
  return geometry?.type === 'Point'
    ? (geometry.coordinates as [number, number])
    : undefined;
}

function toDatetimeLocal(value: string | undefined): string {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromDatetimeLocal(value: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function ensureOption(
  options: { value: string; label: string }[],
  value: string | undefined
) {
  if (!value || options.some((option) => option.value === value)) {
    return options;
  }
  return [...options, { value, label: value }];
}

function FormSelect({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  allowNone,
}: {
  id: string;
  label: string;
  value: string | undefined;
  onChange: (next: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
  allowNone?: boolean;
}) {
  const selectValue = value || (allowNone ? NONE : undefined);
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Select
        value={selectValue}
        onValueChange={(next) => {
          const resolved = String(next);
          onChange(resolved === NONE ? '' : resolved);
        }}
        disabled={disabled}
      >
        <SelectTrigger id={id} size="sm" className="tw:w-full">
          <SelectValue placeholder={placeholder}>
            {options.find((option) => option.value === value)?.label ??
              (allowNone && !value ? 'None' : value)}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="dark">
          {allowNone ? <SelectItem value={NONE}>None</SelectItem> : null}
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

/**
 * The asset form, in both of its modes. Passing no `row` creates an asset;
 * passing one edits it. Create and edit print the same fields, so they share
 * this component rather than drifting apart in two.
 */
export const DHS_ASSET_EDIT_FORM_ID = 'dhs-asset-edit-form';

export function DhsResourceEditForm({
  row,
  groupMembers,
  onDone,
  hideInlineActions = false,
  layout = 'vertical',
}: {
  row?: ResourceRow;
  groupMembers: GroupRosterItem[];
  onDone: (patch?: Partial<ResourceRow>) => void;
  hideInlineActions?: boolean;
  layout?: 'horizontal' | 'vertical';
}) {
  const aorText = useAorText();
  const hwcg = useHwcgFlag();
  const ensureAssetOrgFolders = useEnsureAssetOrgFolders();
  const isCreate = !row;
  const { mutate: updateResourceSet } = useUpdateResourceSet(
    row?.resource_set_id || ''
  );
  const { mutate: createResourceSet } = useCreateResourceSet();
  const { data: resourceSetsData } = useResourceSets();
  const { data: incidents = [] } = useAllIncidents();
  const { data: aors = [] } = useAors();
  const { data: strikeTeams = [] } = useStrikeTeams();
  const { data: automationRules = [] } = useOrgEventAutomationRules();

  const normalizedResourceSetsData = resourceSetsData as
    | ResourceSet[]
    | { rows: ResourceSet[] }
    | undefined;
  const resourceSets = Array.isArray(normalizedResourceSetsData)
    ? normalizedResourceSetsData
    : (normalizedResourceSetsData?.rows ?? []);

  const incidentName = row?.incident?.name || row?.incident_details?.[0]?.name;
  const defaultIncidentId = useMemo(
    () =>
      row?.incident?.id ||
      row?.incident_details?.[0]?.id ||
      incidents.find((incident) => incident.name === incidentName)?.id ||
      '',
    [row?.incident, row?.incident_details, incidentName, incidents]
  );

  const form = useForm<DhsResourceEditFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: row?.name ?? '',
      kind: row?.kind ?? '',
      type: row?.type || '',
      assetStatus: row?.assetStatus || '',
      status: normalizeCheckInStatus(row?.status),
      incidentId: defaultIncidentId,
      quantity: row?.quantity != null ? String(row.quantity) : '',
      owningOrganization: row?.owning_organization || '',
      description: row?.description || '',
      capabilities: row?.capabilities || '',
      address: row?.address || '',
      dateTimeOrdered: toDatetimeLocal(row?.datetimeOrdered),
      pointOfContact: row?.pointOfContact?.userGuid || '',
      unit: row?.unit || '',
      hullTailNumber: row?.hull_tail_number || '',
      aor: row?.aor || '',
      opcon: row?.opcon || '',
      tacon: row?.tacon || '',
      symbology: row?.symbology || '',
      latitude: row?.latitude != null ? String(row.latitude) : '',
      longitude: row?.longitude != null ? String(row.longitude) : '',
      eta: toDatetimeLocal(row?.eta),
      notes: row?.notes || '',
      threats: row?.threats ?? [],
      threatChecklists: row?.threat_checklists ?? {},
    },
  });

  const selectedKind = useWatch({ control: form.control, name: 'kind' });
  const selectedThreats =
    useWatch({ control: form.control, name: 'threats' }) ?? [];
  const threatChecklists =
    useWatch({ control: form.control, name: 'threatChecklists' }) ?? {};
  const selectedIncidentId = useWatch({
    control: form.control,
    name: 'incidentId',
  });
  const isPersonnel = row?.kind === 'Personnel' || selectedKind === 'Personnel';
  const isStatusEditable =
    Boolean(selectedIncidentId) &&
    (isCreate || isCheckInStatusEditable(row?.status));

  const currentResourceSet = resourceSets.find(
    (resourceSet) => resourceSet.resource_set_id === row?.resource_set_id
  );

  // The picker owns the location; `address`, `latitude` and `longitude` stay as
  // form fields so the submit payload is unchanged, but are no longer typed
  // into directly.
  const [geoLocation, setGeoLocation] = useState<GeoLocation | undefined>(() =>
    row?.latitude != null && row?.longitude != null
      ? pointGeoLocation(row.latitude, row.longitude, row.address)
      : undefined
  );
  const [arcgisLink, setArcgisLink] = useState<ArcgisLocationLink | null>(() =>
    parseArcgisLocationLink(row?.arcgis_location_link)
  );

  const handleLocationChange = (next: GeoLocation) => {
    setArcgisLink(null);
    setGeoLocation(next);
    const coords = centroidCoordinates(next.centroid);
    if (coords) {
      form.setValue('longitude', String(coords[0]), { shouldDirty: true });
      form.setValue('latitude', String(coords[1]), { shouldDirty: true });
    }
    form.setValue('address', next.address ?? '', { shouldDirty: true });
  };

  const applyArcgisFeature = (
    next: ArcgisLocationLink,
    point: { longitude: number; latitude: number }
  ) => {
    setArcgisLink(next);
    setGeoLocation(pointGeoLocation(point.latitude, point.longitude));
    form.setValue('longitude', String(point.longitude), { shouldDirty: true });
    form.setValue('latitude', String(point.latitude), { shouldDirty: true });
  };

  const kindOptions = useMemo(
    () =>
      ensureOption(
        USCGResourceKindOptions.filter(
          (option) => option.value !== 'Personnel' || row?.kind === 'Personnel'
        ),
        row?.kind
      ),
    [row?.kind]
  );
  const typeOptions = useMemo(
    () => ensureOption(TypeOptions, row?.type),
    [row?.type]
  );
  const aorOptions = useMemo(
    () => aors.map((aor) => ({ value: aor.id, label: aor.name })),
    [aors]
  );
  const incidentOptions = useMemo(
    () =>
      incidents
        .filter((incident) => incident.id && incident.name)
        .map((incident) => ({ value: incident.id, label: incident.name })),
    [incidents]
  );
  const pocOptions = useMemo(
    () =>
      groupMembers.map((member) => ({
        value: member.user_guid,
        label: member.email_address,
      })),
    [groupMembers]
  );
  const teamOptions = useMemo(
    () =>
      strikeTeams.map((team) => ({
        value: team.strike_team_id || team.id,
        label: team.name,
      })),
    [strikeTeams]
  );
  const symbologyOptions = useMemo(
    () =>
      USCG_SYMBOLOGY_OPTIONS.filter(
        (option) => !EXCLUDED_SYMBOLS.includes(option.type)
      ),
    []
  );

  const onSubmit = form.handleSubmit(async (data) => {
    const lat = Number(data.latitude);
    const lng = Number(data.longitude);
    const geometry =
      data.latitude &&
      data.longitude &&
      !Number.isNaN(lat) &&
      !Number.isNaN(lng)
        ? {
            type: 'Point' as const,
            coordinates: [lng, lat] as [number, number],
          }
        : undefined;

    const sharedPayload = {
      name: data.name,
      kind: data.kind,
      type: data.type,
      assetStatus: data.assetStatus,
      status: persistCheckInStatus(data.status, Boolean(data.incidentId)),
      owningOrganization: data.owningOrganization,
      dateTimeOrdered: fromDatetimeLocal(data.dateTimeOrdered),
      datetimeOrderedOffset: row?.datetimeOrderedOffset ?? 0,
      pointOfContact: data.pointOfContact || undefined,
      address: data.address,
      quantity: data.quantity !== '' ? Number(data.quantity) : undefined,
      unit: data.unit,
      hullTailNumber: data.hullTailNumber,
      symbology: data.symbology || undefined,
      geometry,
      aor: data.aor || undefined,
      opcon: data.opcon || undefined,
      tacon: data.tacon || undefined,
      eta: fromDatetimeLocal(data.eta),
      etaTimezoneOffset: row?.etaTimezoneOffset ?? 0,
      notes: data.notes || undefined,
      ...dualWriteFromRuleSelection(
        data.threats ?? [],
        {
          ...resolveResourceAutomationRuleChecklists(
            currentResourceSet ?? row ?? {},
            automationRules
          ).automationRuleChecklists,
          ...(data.threatChecklists ?? {}),
        },
        automationRules
      ),
      description: data.description,
      capabilities: data.capabilities,
      arcgisLocationLink: arcgisLink,
    };

    if (!row) {
      let documentFolders = DEFAULT_ASSET_DOCUMENT_FOLDERS;
      if (hwcg) {
        try {
          documentFolders = await ensureAssetOrgFolders({
            assetName: data.name,
            folders: DEFAULT_ASSET_DOCUMENT_FOLDERS,
          });
        } catch (error) {
          // The asset is still created; folders are retried when it is opened.
          observability.captureException(error, {
            context: 'createAssetOrgFolders',
          });
        }
      }
      createResourceSet({
        ...sharedPayload,
        incidents: data.incidentId ? [data.incidentId] : [],
        imageUrl: null,
        documentFolders,
      } as unknown as CreateResourceSetFormData);
      onDone();
      return;
    }

    const existingResourceSetPayload = mapResourceSetToEditPayload(
      row.resource_set_id || '',
      currentResourceSet
    );

    updateResourceSet({
      ...existingResourceSetPayload,
      ...sharedPayload,
      incidents: data.incidentId
        ? [data.incidentId]
        : existingResourceSetPayload.incidents,
    });
    onDone({
      name: data.name,
      kind: data.kind,
      type: data.type,
      assetStatus: data.assetStatus,
      status: persistCheckInStatus(data.status, Boolean(data.incidentId)),
      owning_organization: data.owningOrganization,
      datetimeOrdered: fromDatetimeLocal(data.dateTimeOrdered),
      address: data.address,
      quantity: data.quantity !== '' ? Number(data.quantity) : row.quantity,
      unit: data.unit,
      hull_tail_number: data.hullTailNumber,
      symbology: data.symbology,
      latitude: geometry ? lat : row.latitude,
      longitude: geometry ? lng : row.longitude,
      aor: data.aor,
      opcon: data.opcon,
      tacon: data.tacon,
      eta: fromDatetimeLocal(data.eta),
      notes: data.notes,
      ...(() => {
        const dual = dualWriteFromRuleSelection(
          data.threats ?? [],
          {
            ...resolveResourceAutomationRuleChecklists(
              currentResourceSet ?? row,
              automationRules
            ).automationRuleChecklists,
            ...(data.threatChecklists ?? {}),
          },
          automationRules
        );
        return {
          threats: dual.automationRules,
          threat_checklists: dual.automationRuleChecklists,
          automation_rules: dual.automationRules,
          automation_rule_checklists: dual.automationRuleChecklists,
        };
      })(),
      description: data.description,
      capabilities: data.capabilities,
    });
  });

  const handleCancel = () => {
    onDone();
  };

  return (
    <form
      id={hideInlineActions ? DHS_ASSET_EDIT_FORM_ID : undefined}
      className="tw:flex tw:flex-col tw:gap-3"
      onSubmit={onSubmit}
    >
      <AssetMediaSection
        resourceSetId={row?.resource_set_id}
        assetName={row?.name}
        imageUrl={row?.image_url}
        documentFolders={
          row?.document_folders ?? DEFAULT_ASSET_DOCUMENT_FOLDERS
        }
      />
      <Field>
        <FieldLabel htmlFor="dhs-edit-name">Resource Name</FieldLabel>
        <Input
          id="dhs-edit-name"
          disabled={row?.isTemplate}
          {...form.register('name', { required: true })}
        />
      </Field>
      <div
        className={
          layout === 'horizontal'
            ? 'tw:grid tw:grid-cols-2 tw:gap-3'
            : 'tw:grid tw:grid-cols-1 tw:gap-3'
        }
      >
        <Controller
          control={form.control}
          name="kind"
          render={({ field }) => (
            <FormSelect
              id="dhs-edit-kind"
              label="Kind"
              value={field.value}
              onChange={field.onChange}
              options={kindOptions}
              disabled={isPersonnel}
            />
          )}
        />
        <Controller
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormSelect
              id="dhs-edit-type"
              label="Type"
              value={field.value}
              onChange={field.onChange}
              options={typeOptions}
            />
          )}
        />
        <Controller
          control={form.control}
          name="aor"
          render={({ field }) => (
            <FormSelect
              id="dhs-edit-aor"
              label={aorText('AOR')}
              value={field.value}
              onChange={field.onChange}
              options={aorOptions}
              allowNone
              disabled={isPersonnel}
            />
          )}
        />
        <Controller
          control={form.control}
          name="incidentId"
          render={({ field }) => (
            <FormSelect
              id="dhs-edit-incident"
              label="Incident"
              value={field.value}
              onChange={field.onChange}
              options={incidentOptions}
              allowNone
              disabled={isPersonnel}
            />
          )}
        />
        <Controller
          control={form.control}
          name="opcon"
          render={({ field }) => (
            <FormSelect
              id="dhs-edit-opcon"
              label="OPCON"
              value={field.value}
              onChange={field.onChange}
              options={aorOptions}
              allowNone
            />
          )}
        />
        <Controller
          control={form.control}
          name="tacon"
          render={({ field }) => (
            <FormSelect
              id="dhs-edit-tacon"
              label="TACON"
              value={field.value}
              onChange={field.onChange}
              options={aorOptions}
              allowNone
            />
          )}
        />
        <Controller
          control={form.control}
          name="assetStatus"
          render={({ field }) => (
            <FormSelect
              id="dhs-edit-asset-status"
              label="Asset Status"
              value={field.value}
              onChange={field.onChange}
              options={ASSET_STATUS_FILTER_OPTIONS.map((status) => ({
                value: status,
                label: status,
              }))}
              allowNone
            />
          )}
        />
        {isPersonnel ? (
          <Field>
            <FieldLabel>Check-in Status</FieldLabel>
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              Personnel status is managed on the incident roster.
            </p>
          </Field>
        ) : (
          <Controller
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormSelect
                id="dhs-edit-status"
                label="Check-in Status"
                value={field.value}
                onChange={field.onChange}
                options={CHECK_IN_STATUS_OPTIONS}
                allowNone
                disabled={!isStatusEditable}
              />
            )}
          />
        )}
        <Field>
          <FieldLabel htmlFor="dhs-edit-ordered">Datetime Ordered</FieldLabel>
          <Input
            id="dhs-edit-ordered"
            type="datetime-local"
            {...form.register('dateTimeOrdered')}
          />
        </Field>
        <Controller
          control={form.control}
          name="pointOfContact"
          render={({ field }) => (
            <FormSelect
              id="dhs-edit-poc"
              label="Point of Contact"
              value={field.value}
              onChange={field.onChange}
              options={pocOptions}
              allowNone
            />
          )}
        />
        <Field>
          <FieldLabel htmlFor="dhs-edit-org">Owning Organization</FieldLabel>
          <Input id="dhs-edit-org" {...form.register('owningOrganization')} />
        </Field>
        <Field>
          <FieldLabel htmlFor="dhs-edit-hull">Hull / Tail Number</FieldLabel>
          <Input id="dhs-edit-hull" {...form.register('hullTailNumber')} />
        </Field>
        <Field>
          <FieldLabel htmlFor="dhs-edit-qty">Quantity</FieldLabel>
          <Input
            id="dhs-edit-qty"
            type="number"
            {...form.register('quantity', { required: isCreate })}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="dhs-edit-eta">
            Estimated Time of Arrival
          </FieldLabel>
          <Input
            id="dhs-edit-eta"
            type="datetime-local"
            {...form.register('eta')}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="dhs-edit-unit">Unit</FieldLabel>
          <Input id="dhs-edit-unit" {...form.register('unit')} />
        </Field>
      </div>
      <Field>
        <FieldLabel htmlFor="dhs-edit-description">Description</FieldLabel>
        <Textarea id="dhs-edit-description" {...form.register('description')} />
      </Field>
      <Field>
        <FieldLabel htmlFor="dhs-edit-capabilities">Capabilities</FieldLabel>
        <Textarea
          id="dhs-edit-capabilities"
          {...form.register('capabilities')}
        />
      </Field>
      <Controller
        control={form.control}
        name="symbology"
        render={({ field }) => {
          const selected = symbologyOptions.find(
            (option) => option.type === field.value
          );
          return (
            <Field>
              <FieldLabel htmlFor="dhs-edit-symbology">Symbology</FieldLabel>
              <Select
                value={field.value || NONE}
                onValueChange={(next) =>
                  field.onChange(String(next) === NONE ? '' : String(next))
                }
              >
                <SelectTrigger
                  id="dhs-edit-symbology"
                  size="sm"
                  className="tw:w-full"
                >
                  <SelectValue>
                    {selected ? (
                      <span className="tw:inline-flex tw:items-center tw:gap-2">
                        {selected.iconSrc ? (
                          <img
                            src={selected.iconSrc}
                            alt=""
                            className="symbology-icon"
                          />
                        ) : null}
                        {selected.label}
                      </span>
                    ) : (
                      'None'
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="dark">
                  <SelectItem value={NONE}>None</SelectItem>
                  {symbologyOptions.map((option) => (
                    <SelectItem key={option.type} value={option.type}>
                      <span className="tw:inline-flex tw:items-center tw:gap-2">
                        {option.iconSrc ? (
                          <img
                            src={option.iconSrc}
                            alt=""
                            className="symbology-icon"
                          />
                        ) : null}
                        {option.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          );
        }}
      />
      <Field>
        <FieldLabel>Current Location</FieldLabel>
        <GeoLocationComponent
          location={geoLocation}
          setLocation={handleLocationChange}
        />
      </Field>
      <ArcgisFeatureLocationField
        link={arcgisLink}
        onSelectFeature={applyArcgisFeature}
        onClear={() => setArcgisLink(null)}
      />
      <Field>
        <FieldLabel htmlFor="dhs-edit-notes">Notes</FieldLabel>
        <Textarea id="dhs-edit-notes" {...form.register('notes')} />
      </Field>
      <Field>
        <FieldLabel>Automation rules</FieldLabel>
        <Controller
          control={form.control}
          name="threats"
          render={({ field }) => (
            <ResourceThreatsMultiSelect
              value={field.value ?? []}
              onChange={field.onChange}
            />
          )}
        />
      </Field>
      {selectedThreats.map((threat) => (
        <Field key={threat}>
          <FieldLabel>Response Checklist — {threat}</FieldLabel>
          <ChecklistConfigEditor
            items={threatChecklists[threat] ?? []}
            onChange={(next) =>
              form.setValue(
                'threatChecklists',
                {
                  ...form.getValues('threatChecklists'),
                  [threat]: next,
                },
                { shouldDirty: true }
              )
            }
            userOptions={pocOptions}
            teamOptions={teamOptions}
          />
        </Field>
      ))}
      {hideInlineActions ? null : (
        <div className="tw:flex tw:justify-end tw:gap-2">
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="submit">{isCreate ? 'Create' : 'Save'}</Button>
        </div>
      )}
    </form>
  );
}
